import ELK, { type ElkExtendedEdge, type ElkNode } from "elkjs/lib/elk.bundled.js";
import type { Edge, Node } from "@xyflow/react";
import { nodeText, type GraphEdge, type GraphNode, type TriggerGraph } from "./types";

const elk = new ELK();

export const NODE_WIDTH = 168;
export const NODE_HEIGHT = 40;
const GROUP_PADDING = 28;

export interface FlowNodeData {
  node: GraphNode;
  [key: string]: unknown;
}

export interface FlowGroupData {
  title: string;
  phase?: string;
  [key: string]: unknown;
}

export type FlowNode = Node<FlowNodeData, "rsNode"> | Node<FlowGroupData, "rsGroup">;

const PHASE_ORDER = ["setup", "tick", "finish"];

export const PHASE_WORDS: Record<string, string> = {
  setup: "setup · once, at the start",
  tick: "tick · every second of the flight",
  finish: "finish · once, at the end",
};

function nodeWidth(n: GraphNode, plain: boolean): number {
  const shown = nodeText(n, plain);
  const text = shown.length + (n.badge ? n.badge.length + 2 : 0);
  // plain sentences are proportional (narrower per char) and are allowed a bit more room
  const perChar = plain && n.text ? 7.2 : 9;
  const cap = plain && n.text ? 360 : 320;
  return Math.min(cap, Math.max(NODE_WIDTH, perChar * text + 40));
}

function groupIdFor(n: GraphNode): string | null {
  if (!n.trigger || !n.phase) return null;
  return `group:${n.trigger}/${n.phase}`;
}

/**
 * Lay out a TriggerGraph with ELK (layered, left-to-right). Nodes of one trigger phase share a
 * compound group so setup / tick / finish read as separate lanes; trigger and report nodes sit
 * at the top level. Returns React Flow nodes (absolute positions) and edges.
 */
export async function layoutGraph(
  graph: TriggerGraph,
  options: { collapsedPhases?: Set<string>; hiddenKinds?: Set<string>; plain?: boolean } = {},
): Promise<{ nodes: FlowNode[]; edges: Edge[] }> {
  const hidden = options.hiddenKinds ?? new Set<string>();
  const collapsed = options.collapsedPhases ?? new Set<string>();
  const plain = options.plain ?? true;
  const visible = graph.nodes.filter((n) => !hidden.has(n.kind));
  const visibleIds = new Set(visible.map((n) => n.id));

  const groups = new Map<string, { title: string; phase: string; children: GraphNode[] }>();
  const topLevel: GraphNode[] = [];
  for (const n of visible) {
    const gid = groupIdFor(n);
    if (!gid) {
      topLevel.push(n);
      continue;
    }
    if (collapsed.has(gid)) continue;
    const phaseTitle = plain ? (PHASE_WORDS[n.phase!] ?? n.phase) : n.phase;
    const g = groups.get(gid) ?? { title: `${n.trigger} · ${phaseTitle}`, phase: n.phase!, children: [] };
    g.children.push(n);
    groups.set(gid, g);
  }

  const elkChildren: ElkNode[] = [];
  for (const n of topLevel) {
    elkChildren.push({ id: n.id, width: nodeWidth(n, plain), height: NODE_HEIGHT });
  }
  const sortedGroups = [...groups.entries()].sort(
    (a, b) => PHASE_ORDER.indexOf(a[1].phase) - PHASE_ORDER.indexOf(b[1].phase),
  );
  for (const [gid, g] of sortedGroups) {
    elkChildren.push({
      id: gid,
      layoutOptions: {
        "elk.algorithm": "layered",
        "elk.direction": "RIGHT",
        "elk.padding": `[top=${GROUP_PADDING + 8},left=${GROUP_PADDING},bottom=${GROUP_PADDING},right=${GROUP_PADDING}]`,
        "elk.spacing.nodeNode": "18",
        "elk.layered.spacing.nodeNodeBetweenLayers": "42",
      },
      children: g.children.map((n) => ({ id: n.id, width: nodeWidth(n, plain), height: NODE_HEIGHT })),
    });
  }

  const elkEdges: ElkExtendedEdge[] = [];
  for (const e of graph.edges) {
    if (!visibleIds.has(e.source) || !visibleIds.has(e.target)) continue;
    const src = graph.nodes.find((n) => n.id === e.source);
    const dst = graph.nodes.find((n) => n.id === e.target);
    if (!src || !dst) continue;
    const sg = groupIdFor(src);
    const dg = groupIdFor(dst);
    if ((sg && collapsed.has(sg)) || (dg && collapsed.has(dg))) continue;
    elkEdges.push({ id: e.id, sources: [e.source], targets: [e.target] });
  }

  const root: ElkNode = {
    id: "root",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction": "RIGHT",
      "elk.hierarchyHandling": "INCLUDE_CHILDREN",
      "elk.spacing.nodeNode": "28",
      "elk.layered.spacing.nodeNodeBetweenLayers": "56",
      "elk.spacing.componentComponent": "40",
      "elk.layered.crossingMinimization.semiInteractive": "true",
      "elk.edgeRouting": "SPLINES",
    },
    children: elkChildren,
    edges: elkEdges,
  };

  const laid = await elk.layout(root);
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const flowNodes: FlowNode[] = [];

  const place = (child: ElkNode, offsetX: number, offsetY: number) => {
    const x = (child.x ?? 0) + offsetX;
    const y = (child.y ?? 0) + offsetY;
    if (child.children && child.children.length) {
      const g = groups.get(child.id);
      flowNodes.push({
        id: child.id,
        type: "rsGroup",
        position: { x, y },
        data: { title: g?.title ?? child.id, phase: g?.phase },
        // explicit dimensions: the graph is controlled without onNodesChange, so React Flow's
        // measurements never reach these objects and the minimap would otherwise skip them
        width: child.width ?? 0,
        height: child.height ?? 0,
        style: { width: child.width ?? 0, height: child.height ?? 0 },
        draggable: false,
        selectable: false,
        zIndex: -1,
      });
      for (const c of child.children) place(c, x, y);
      return;
    }
    const gn = byId.get(child.id);
    if (!gn) return;
    flowNodes.push({
      id: child.id,
      type: "rsNode",
      position: { x, y },
      data: { node: gn },
      width: child.width ?? NODE_WIDTH,
      height: child.height ?? NODE_HEIGHT,
      style: { width: child.width ?? NODE_WIDTH, height: child.height ?? NODE_HEIGHT },
      draggable: false,
    });
  };
  for (const c of laid.children ?? []) place(c, 0, 0);

  const flowEdges: Edge[] = elkEdges.map((ee) => {
    const ge = graph.edges.find((e) => e.id === ee.id) as GraphEdge;
    return {
      id: ge.id,
      source: ge.source,
      target: ge.target,
      label: ge.label ?? undefined,
      type: "smoothstep",
      className: `rs-edge rs-edge-${ge.kind}`,
      animated: false,
      data: { kind: ge.kind },
    };
  });
  return { nodes: flowNodes, edges: flowEdges };
}
